import { findTimeSuggestions, type IgnoredTerm, type StoryNode } from '@dispositio/core';
import type { Node as PMNode } from '@tiptap/pm/model';
import { PluginKey } from '@tiptap/pm/state';
import { suggestExtension, type SuggestLive, type SuggestState } from './suggest';

/**
 * Szene teilen, wo der Text es nahelegt (PF 51, AP-24): ein Szenentrenner (Trennlinie, „* * *“, „⁂“, „#“) bzw. ein Zeitsprung am
 * Absatzanfang („Drei Tage später …“, auch als Zeitmarke) mitten im Draft – gelb als Vorschlag „Hier eine neue Szene beginnen“. ✓ teilt den
 * Draft: der Teil danach wird eine eigene Szene direkt dahinter (Zeit und Ort aus seinem Text), ✕ merkt „nicht teilen“ an dieser Stelle.
 */

/** Ein Absatz der obersten Ebene, wie ihn die Erkennung liest */
export interface SplitBlock {
  /** Trennlinie bzw. Absatz, der nur ein Szenentrenner ist */
  rule: boolean;
  text: string;
  /** Zeitangabe bzw. Zeitmarke am Absatzanfang: [von, bis) im Absatz */
  timeAt: { from: number; to: number } | null;
}

/** Ein Vorschlag: vor Absatz \`index\` teilen; \`drop\`: der Absatz davor ist der Trenner (fällt weg) */
export interface SplitCandidate {
  index: number;
  kind: 'separator' | 'time';
  drop: number | null;
  /** woran die Stelle gemerkt wird (Anfang des Absatzes) */
  key: string;
}

const SEPARATOR = /^\s*(?:(?:\*\s*){3,}|(?:-\s*){3,}|(?:_\s*){3,}|⁂|#|~{3,}|◇|❖|\*)\s*$/u;
/** Ist dieser Absatz nur ein Szenentrenner? */
export const isSeparator = (text: string): boolean => SEPARATOR.test(text);

/** Woran ein Vorschlag gemerkt wird: der Anfang des Absatzes, ab dem geteilt würde */
export const splitKey = (text: string): string => text.trim().replace(/\s+/g, ' ').slice(0, 40);

/** „Nicht teilen“, gemerkt im Dokument für die Stelle mit diesem Absatzanfang */
export const splitIgnore = (key: string): IgnoredTerm => ({ term: '~teilen', before: '', after: key });

/**
 * Wo sich der Draft teilen ließe: hinter einem Szenentrenner (mit Text davor und danach) bzw. vor einem Absatz, der mit einer Zeitangabe
 * beginnt (nicht der erste mit Text). Ohne, was als „nicht teilen“ gemerkt ist.
 */
export function splitCandidates(blocks: readonly SplitBlock[], ignored?: readonly IgnoredTerm[]): SplitCandidate[] {
  const out: SplitCandidate[] = [];
  const has = (i: number): boolean => blocks[i]!.text.trim() !== '' && !blocks[i]!.rule;
  const skip = (key: string): boolean => !!ignored?.some((x) => x.term === '~teilen' && x.after === key);
  let seenText = false;
  for (let i = 0; i < blocks.length; i++) {
    const b = blocks[i]!;
    if (b.rule) {
      let next = i + 1;
      while (next < blocks.length && !has(next) && !blocks[next]!.rule) next++;
      if (seenText && next < blocks.length && has(next)) {
        const key = splitKey(blocks[next]!.text);
        if (!skip(key)) out.push({ index: next, kind: 'separator', drop: i, key });
        i = next - 1;
      }
      continue;
    }
    if (!has(i)) continue;
    // derselbe Absatz hinter einem Szenentrenner: schon vorgeschlagen
    if (seenText && b.timeAt && b.timeAt.from <= 1 && out[out.length - 1]?.index !== i) {
      const key = splitKey(b.text);
      if (!skip(key)) out.push({ index: i, kind: 'time', drop: null, key });
    }
    seenText = true;
  }
  return out;
}

/** Ein Vorschlag im Editor: markiert ist der Anfang des neuen Teils; geteilt wird bei \`cutFrom\` (alter Teil endet) bzw. \`cutTo\` (neuer beginnt) */
export interface SplitHit {
  from: number;
  to: number;
  cutFrom: number;
  cutTo: number;
  kind: 'separator' | 'time';
  key: string;
}

export interface SplitLive extends SuggestLive<SplitHit> {
  scene: StoryNode | null;
  ignored: readonly IgnoredTerm[] | undefined;
  title: (hit: SplitHit) => string;
}

export const sceneSplitKey = new PluginKey<SuggestState<SplitHit>>('sceneSplit');

/** Das erste Wort eines Absatzes (für die Stelle eines Szenentrenners) */
const firstWord = (text: string): { from: number; to: number } => {
  const m = /\S+/.exec(text);
  return m ? { from: m.index, to: m.index + m[0].length } : { from: 0, to: 0 };
};

function splitHits(doc: PMNode, l: SplitLive): SplitHit[] {
  if (!l.scene) return [];
  const blocks: (SplitBlock & { pos: number; size: number })[] = [];
  doc.forEach((node, pos) => {
    if (node.type.name === 'horizontalRule') return void blocks.push({ rule: true, text: '', timeAt: null, pos, size: node.nodeSize });
    if (!node.isTextblock || node.type.spec.code) return void blocks.push({ rule: false, text: '', timeAt: null, pos, size: node.nodeSize });
    const text = node.textBetween(0, node.content.size, '\n', '\n');
    // Zeitmarke am Anfang (sonst eine erkannte Zeitangabe)
    let mark: { from: number; to: number } | null = null;
    node.forEach((child, offset) => {
      if (mark || !child.isText || offset > 1) return;
      if (child.marks.some((m) => m.type.name === 'timeMark')) mark = { from: offset, to: offset + child.nodeSize };
    });
    const found = mark ?? findTimeSuggestions(text).find((s) => s.from <= 1) ?? null;
    blocks.push({ rule: isSeparator(text), text, timeAt: found ? { from: found.from, to: found.to } : null, pos, size: node.nodeSize });
  });
  return splitCandidates(blocks, l.ignored).map((c) => {
    const b = blocks[c.index]!;
    const at = c.kind === 'time' && b.timeAt ? b.timeAt : firstWord(b.text);
    const cutFrom = c.drop !== null ? blocks[c.drop]!.pos : b.pos;
    return { from: b.pos + 1 + at.from, to: b.pos + 1 + Math.max(at.to, at.from + 1), cutFrom, cutTo: b.pos, kind: c.kind, key: c.key };
  });
}

export const sceneSplitMarks = (live: { current: SplitLive }) =>
  suggestExtension<SplitHit, SplitLive>(
    {
      name: 'sceneSplit',
      kind: 'split',
      key: sceneSplitKey,
      hits: splitHits,
      mark: (h, l) => ({ class: 'rt-scene-split', 'data-scene-split': h.kind, title: l.title(h) }),
      word: (h) => h.key,
      testId: 'split',
    },
    live,
  );
